<template>
  <div class="page-view">

    <BasePageHeader :title="$t('maintenance.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="isOperatorOrAbove" to="/maintenance/schedules/new" class="btn primary small">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-4 h-4">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {{ $t('maintenance.new') }}
        </router-link>
      </template>
    </BasePageHeader>

    <!-- Tab strip -->
    <div class="flex border-b border-[var(--color-border)]">
      <router-link
        to="/maintenance/schedules"
        class="flex-1 text-center px-2 py-3 text-[.875rem] font-semibold no-underline border-b-2 transition-colors text-[var(--color-accent)] border-b-[var(--color-primary)]"
      >{{ $t('maintenance.schedules') }}</router-link>
      <router-link
        to="/maintenance/history"
        class="flex-1 text-center px-2 py-3 text-[.875rem] font-semibold no-underline border-b-2 border-b-transparent text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors"
      >{{ $t('maintenance.serviceHistory') }}</router-link>
    </div>

    <!-- Filters -->
    <div class="flex gap-2 items-center flex-wrap px-4 py-3 border-b border-[var(--color-border)]">
      <BaseFormField density="compact" class="flex-1 min-w-[120px] !mb-0">
        <BaseSelect v-model="filters.maintenance_type" density="compact" :aria-label="$t('maintenance.allTypes')">
          <option value="">{{ $t('maintenance.allTypes') }}</option>
          <option v-for="mt in MAINT_TYPES" :key="mt.value" :value="mt.value">{{ mt.label }}</option>
        </BaseSelect>
      </BaseFormField>
      <button
        class="px-3 py-1.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-[var(--color-muted)] text-[.8rem] font-semibold cursor-pointer whitespace-nowrap transition-colors"
        :class="filters.overdue ? 'bg-[var(--color-primary)]/20 text-[var(--color-accent)] border-[var(--color-primary)]' : 'hover:bg-[var(--color-surface-2)]'"
        @click="filters.overdue = !filters.overdue; load()"
      >{{ $t('maintenance.overdue') }}</button>
      <button
        class="px-3 py-1.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-[var(--color-muted)] text-[.8rem] font-semibold cursor-pointer whitespace-nowrap transition-colors"
        :class="filters.is_active ? 'bg-[var(--color-primary)]/20 text-[var(--color-accent)] border-[var(--color-primary)]' : 'hover:bg-[var(--color-surface-2)]'"
        @click="filters.is_active = !filters.is_active; load()"
      >{{ $t('maintenance.active') }}</button>
      <button class="btn primary small" @click="load">{{ $t('common.apply') }}</button>
    </div>

    <!-- Loading / error / empty -->
    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />
    <EmptyState v-else-if="!schedules.length" :message="$t('maintenance.noSchedules')" />

    <!-- List -->
    <div v-else class="list-stack">
      <div
        v-for="s in schedules" :key="s.id"
        class="data-card flex-col gap-2 !items-start"
      >
        <div class="flex flex-col gap-1">
          <div class="flex items-start justify-between gap-2">
            <div class="data-card__name">{{ s.title }}</div>
            <div class="flex gap-1 flex-wrap shrink-0">
              <span v-if="s.maintenance_type" class="badge" :class="typeBadgeClass(s.maintenance_type)">
                {{ MAINT_LABELS[s.maintenance_type] ?? s.maintenance_type }}
              </span>
              <span v-if="s.is_overdue" class="badge badge-danger">{{ $t('common.overdue') }}</span>
              <span v-if="!s.is_active" class="badge badge-muted">{{ $t('common.closed') }}</span>
            </div>
          </div>
          <div class="flex flex-wrap gap-1.5 items-center mt-0.5">
            <span v-if="s.next_due_date" class="badge" :class="s.is_overdue ? 'badge-danger' : 'badge-muted'">{{ $t('maintenance.due', { date: formatDate(s.next_due_date) }) }}</span>
            <span v-if="s.frequency" class="badge badge-muted">{{ FREQ_LABELS[s.frequency] ?? s.frequency }}</span>
            <span v-if="s.frequency === 'custom' && s.frequency_days" class="badge badge-muted">{{ $t('maintenance.every', { n: s.frequency_days }) }}</span>
            <span v-if="s.assigned_to" class="badge badge-muted">{{ userName(s.assigned_to) }}</span>
          </div>
          <div v-if="s.items?.length" class="flex flex-wrap gap-1 mt-1">
            <span v-for="inst in s.items" :key="inst.id ?? inst" class="badge badge-muted badge-pill">{{ instanceLabel(inst) }}</span>
          </div>
          <div v-else-if="s.item_metadata" class="flex flex-wrap gap-1 mt-1">
            <span class="badge badge-muted badge-pill">{{ instanceLabel(s.item_metadata) }}</span>
          </div>
          <div v-if="s.description || s.notes" class="text-[.78rem] text-[var(--color-muted)] mt-1">{{ s.description || s.notes }}</div>
        </div>

        <!-- Row actions -->
        <div class="flex gap-2 flex-wrap pt-2 border-t border-[var(--color-border)]">
          <router-link v-if="s.is_active && isOperatorOrAbove" :to="`/maintenance/schedules/${s.id}/complete`" class="btn primary small">
            {{ $t('maintenance.markComplete') }}
          </router-link>
          <router-link v-if="isOperatorOrAbove" :to="`/maintenance/schedules/${s.id}/edit`" class="btn ghost small">{{ $t('common.edit') }}</router-link>
          <button v-if="isOperatorOrAbove" class="btn danger small" @click="openDeleteSchedule(s)">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

  </div>

  <!-- Delete confirm -->
  <BaseConfirmModal
    :show="!!deleteScheduleTarget"
    :title="$t('maintenance.deleteSchedule')"
    :body="deleteScheduleTarget ? $t('maintenance.deleteScheduleBody', { title: deleteScheduleTarget.title }) : ''"
    :confirm-label="$t('common.delete')"
    confirm-variant="delete"
    :loading="deletingSchedule"
    @confirm="confirmDeleteSchedule"
    @cancel="deleteScheduleTarget = null"
  />

</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import {
  getMaintenanceSchedules, deleteMaintenanceSchedule,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'

const toast = useToast()
const route = useRoute()
const { t } = useI18n()
const { isOperatorOrAbove } = usePermissions()

const MAINT_TYPES = computed(() => [
  { value: 'preventive',  label: t('maintenance.types.preventive') },
  { value: 'corrective',  label: t('maintenance.types.corrective') },
  { value: 'inspection',  label: t('maintenance.types.inspection') },
  { value: 'calibration', label: t('maintenance.types.calibration') },
])
const MAINT_LABELS = computed(() => Object.fromEntries(MAINT_TYPES.value.map(x => [x.value, x.label])))

const FREQ_LABELS = computed(() => ({
  daily:     t('maintenance.frequencies.daily'),
  weekly:    t('maintenance.frequencies.weekly'),
  monthly:   t('maintenance.frequencies.monthly'),
  quarterly: t('maintenance.frequencies.quarterly'),
  annual:    t('maintenance.frequencies.annual'),
  custom:    t('maintenance.frequencies.custom'),
}))

const schedules  = ref([])
const loading    = ref(false)
const loadError  = ref(null)

const filters = ref({
  maintenance_type: '',
  overdue:          false,
  is_active:        true,
  item_metadata:    route.query.instance ?? '',
})

watch(() => route.query.instance, (val) => {
  filters.value.item_metadata = val ?? ''
  load()
}, { immediate: false })

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const params = {}
    if (filters.value.maintenance_type) params.maintenance_type = filters.value.maintenance_type
    if (filters.value.overdue)          params.overdue           = true
    if (filters.value.is_active)        params.is_active         = true
    if (filters.value.item_metadata)    params.item_metadata     = filters.value.item_metadata
    const res = await getMaintenanceSchedules(params)
    schedules.value = res.results ?? res ?? []
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('maintenance.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)

// ── delete schedule ───────────────────────────────────────────────────
const deleteScheduleTarget = ref(null)
const deletingSchedule     = ref(false)

function openDeleteSchedule(s) { deleteScheduleTarget.value = s }

async function confirmDeleteSchedule() {
  deletingSchedule.value = true
  try {
    await deleteMaintenanceSchedule(deleteScheduleTarget.value.id)
    toast.success(t('maintenance.deleted'))
    deleteScheduleTarget.value = null
    await load()
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('maintenance.deleteFailed'))
  } finally {
    deletingSchedule.value = false
  }
}

// ── helpers ───────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '—'
  try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
  catch { return d }
}

function userName(u) {
  if (!u) return ''
  if (typeof u === 'object') return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `#${u.id}`
  return String(u)
}

function instanceLabel(inst) {
  if (!inst) return ''
  if (typeof inst === 'object') return inst.asset_tag ?? inst.serial_number ?? inst.item?.name ?? `#${inst.id}`
  return `#${inst}`
}

function typeBadgeClass(t) {
  return {
    preventive:  'badge-accent',
    corrective:  'badge-warning',
    inspection:  'badge-muted',
    calibration: 'badge-accent',
  }[t] ?? 'badge-muted'
}
</script>
